/* Fenêtre de confirmation lors d'un changement d'étape. */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Check, Download, Landmark, Loader2, Mail, Zap } from 'lucide-react';
import type { CrmContact, CrmContactDraft, Directory, SharingOperation, SibelgaStatus, Stage } from './types';
import { SibelgaStatusPicker } from './SibelgaStatus';
import { STAGES, STAGE_INDEX, displayName, isSepaEligible, mandateState } from './types';
import { ibanError, isValidBic, maskIban } from '../../../supabase/functions/_shared/sepa';
import { firstDayOfNextMonth, todayIso } from './validation';
import {
  createParticipantsForContact, downloadBase64Pdf, fetchGroupPrice, findGroupCard, generateContract, groupContactPatch,
  mergeIntoGroupCard, sendContractEmail, sendCustomMessage, sendInvitation, sendWelcome, targetGroupFor, updateContact, uploadSignedContract,
  type EmailContent, previewContractEmail, previewMessage,
} from './api';
import { EmailPreview } from './EmailPreview';
import {
  DEFAULT_TRIGGERS, TRIGGER_TYPES, type TriggerAction, type TriggerSettings, type TriggerStage, actionLabel, fetchTriggerSettings,
} from './triggers';
import { Field, ModalShell, inputClass, errorMessage } from './ui';
import { VAT_RATE_OPTIONS, defaultVatRate } from './participants';
import {
  type SavingsEstimateSettings, contractEstimate, fetchSavingsSettings, formatEuros, formatKwh, formatPrice,
} from './savingsEstimate';

interface Props {
  contact: CrmContact;
  target: Stage;
  operations: SharingOperation[];
  directory: Directory;
  onCancel: () => void;
  /** `others` : autres contacts modifiés (carte du groupe en cas de rattachement). */
  onDone: (c: CrmContact, others?: CrmContact[]) => void;
}

export function TransitionDialog({ contact: c, target, operations, directory, onCancel, onDone }: Props) {
  const from = STAGE_INDEX[c.stage];
  const to = STAGE_INDEX[target];
  const backward = to < from;
  /** L'étape `s` est franchie par ce déplacement. */
  const crosses = (s: Stage) => from < STAGE_INDEX[s] && STAGE_INDEX[s] <= to;
  const isProsumer = c.role === 'prosumer';

  const [sendByEmail, setSendByEmail] = useState(target === 'contract_sent');
  const sendTouched = useRef(false);

  /* Mandat SEPA (particulier consommateur) : inclus dans le contrat si le membre a accepté la domiciliation. */
  const sepaEligible = isSepaEligible(c);
  const mandate = mandateState(c);
  const [includeMandate, setIncludeMandate] = useState(sepaEligible && !!c.sepa_consent && mandate !== 'active');
  const withMandate = sepaEligible && includeMandate;
  const bankProblems = [
    ibanError(c.iban),
    !c.account_holder?.trim() ? 'titulaire du compte manquant' : null,
    !isValidBic(c.bic) ? 'BIC manquant ou invalide' : null,
  ].filter((v): v is string => !!v);
  /* Contrat signé : le mandat envoyé avec le contrat devient actif (date de signature : celle du contrat par défaut). */
  const mandatePending = sepaEligible && mandate === 'pending';
  const [mandateSigned, setMandateSigned] = useState(true);
  const [mandateDate, setMandateDate] = useState<string | null>(null);

  /* Estimation des économies demandée dans le formulaire : jointe à l'e-mail du contrat si le réglage est activé. */
  const [savings, setSavings] = useState<SavingsEstimateSettings | null>(null);
  useEffect(() => {
    if (target !== 'contract_sent' || !c.estimate_requested) return;
    fetchSavingsSettings().then(setSavings).catch(() => setSavings(null));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const estimate = savings ? contractEstimate(c, savings) : null;

  /* Déclencheurs de l'étape d'arrivée (CRM → Paramètres → Déclencheurs) : actions cochées par défaut, décochables ici. */
  const [triggers, setTriggers] = useState<TriggerSettings | null>(null);
  const [actionChecks, setActionChecks] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (target === 'lead') return;
    fetchTriggerSettings()
      .catch(err => { toast.error(`Déclencheurs : ${errorMessage(err)}`); return DEFAULT_TRIGGERS; })
      .then(t => {
        setTriggers(t);
        const stageActions = t[target as TriggerStage].actions;
        setActionChecks(Object.fromEntries(stageActions.filter(a => a.enabled && a.type !== 'send_contract').map(a => [a.id, true])));
        if (target === 'contract_sent' && !sendTouched.current) setSendByEmail(stageActions.some(a => a.type === 'send_contract' && a.enabled));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const stageTriggers = target !== 'lead' ? (triggers ?? DEFAULT_TRIGGERS)[target as TriggerStage] : null;
  /** Actions e-mail proposées (le contrat a son propre bloc). */
  const messageActions: TriggerAction[] = triggers && stageTriggers ? stageTriggers.actions.filter(a => a.enabled && a.type !== 'send_contract') : [];
  const [signedDate, setSignedDate] = useState(c.contract_signed_at ?? todayIso());
  const [signedFile, setSignedFile] = useState<File | null>(null);
  // Encodage Sibelga : date du jour (ou celle déjà enregistrée), modifiable ensuite dans la fiche.
  const encodedDate = c.sibelga_encoded_at ?? todayIso();
  const [operationId, setOperationId] = useState(c.sharing_operation_id ?? '');
  // Passage en « Encodé Sibelga » : le dossier est soumis par défaut.
  const [sibelgaStatus, setSibelgaStatus] = useState<SibelgaStatus[]>(c.sibelga_status?.length ? c.sibelga_status : ['submitted']);
  const [startDate, setStartDate] = useState(c.sharing_start_date ?? firstDayOfNextMonth(c.sibelga_encoded_at ?? todayIso()));
  const [price, setPrice] = useState(String(c.sharing_price_cents_kwh ?? c.price_cents_kwh ?? ''));
  const [sellPrice, setSellPrice] = useState(String(c.sharing_sell_price_cents_kwh ?? c.sell_price_cents_kwh ?? ''));
  const [vatRate, setVatRate] = useState<number>(c.sharing_vat_rate ?? defaultVatRate(c));
  const [working, setWorking] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  /* E-mails relus et modifiés pour cet envoi (clé : « contract » ou id de l'action). */
  const [edits, setEdits] = useState<Record<string, EmailContent | null>>({});
  const setEdit = (key: string) => (v: EmailContent | null) => setEdits(prev => ({ ...prev, [key]: v }));

  const targetLabel = STAGES.find(s => s.key === target)?.label ?? target;
  const name = displayName(c);
  const pendingMeters = c.meters.filter(m => !m.participant_id).length;
  const plannedGroup = targetGroupFor(c);
  const knownGroup = plannedGroup ? directory.groups.find(g => g.name.toLowerCase() === plannedGroup.toLowerCase()) : undefined;
  const validPrice = (v: string) => v.trim() !== '' && Number(v) > 0 && Number(v) < 100;

  /* Passage en « Membre actif » dans un groupe existant : prix du groupe d'office, contact du groupe affiché. */
  const priceTouched = useRef(false);
  const [groupCard, setGroupCard] = useState<CrmContact | null>(null);
  const [groupPriceApplied, setGroupPriceApplied] = useState(false);
  useEffect(() => {
    if (backward || !crosses('active') || !plannedGroup) return;
    let cancelled = false;
    findGroupCard(plannedGroup, c.id).then(card => { if (!cancelled) setGroupCard(card); }).catch(() => {});
    if (knownGroup && c.sharing_price_cents_kwh === null) {
      fetchGroupPrice(knownGroup.name, c.role, c.id)
        .then(gp => {
          if (cancelled || !gp || priceTouched.current) return;
          if (gp.price !== null) setPrice(String(gp.price));
          if (isProsumer && gp.sellPrice !== null) setSellPrice(String(gp.sellPrice));
          if (gp.price !== null || (isProsumer && gp.sellPrice !== null)) setGroupPriceApplied(true);
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const contractPrice = c.price_cents_kwh;
  const contactPatch = groupCard ? groupContactPatch(groupCard, c) : {};

  const confirmBackward = async () => {
    setWorking(true);
    try {
      const saved = await updateContact(c.id, { stage: target });
      toast.success(`${name} : retour à « ${targetLabel} »`);
      onDone(saved);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  const confirmForward = async () => {
    if (crosses('contract_signed') && !signedDate) { toast.error('Indiquez la date de signature'); return; }
    if (target === 'contract_sent' && sendByEmail && withMandate && bankProblems.length > 0) {
      toast.error(`Mandat SEPA : ${bankProblems.join(', ')}. Complétez la fiche ou désactivez « Inclure le mandat SEPA ».`);
      return;
    }
    if (crosses('sibelga') && !operationId) { toast.error("Indiquez l'opération de partage"); return; }
    if (crosses('active')) {
      if (!startDate) { toast.error('Indiquez la date de début de partage'); return; }
      if (c.meters.some(m => !m.participant_id && !m.name?.trim())) {
        toast.error('Indiquez le nom du participant de chaque compteur dans la fiche');
        return;
      }
      if (!validPrice(price)) { toast.error('Indiquez un prix entre 0 et 100 c€/kWh'); return; }
      if (isProsumer && !validPrice(sellPrice)) { toast.error('Indiquez un prix de vente entre 0 et 100 c€/kWh'); return; }
    }

    setWorking(true);
    try {
      const patch: CrmContactDraft = { stage: target };
      // Statut d'attente de l'étape d'arrivée (réglable dans Paramètres → Déclencheurs).
      if (stageTriggers) {
        patch.waiting_on = stageTriggers.waiting ?? null;
        patch.waiting_since = patch.waiting_on ? new Date().toISOString() : null;
        if (patch.waiting_on !== c.waiting_on) patch.waiting_note = null;
      }

      if (crosses('contract_sent')) {
        if (target === 'contract_sent' && sendByEmail) {
          const { warnings, mandateReference, estimate: sentEstimate } = await sendContractEmail(c, operations, { sepa: withMandate, content: edits.contract ?? undefined });
          toast.success(`${mandateReference ? `Contrat et mandat SEPA ${mandateReference} envoyés` : 'Contrat envoyé'} à ${c.email}${
            sentEstimate ? `, avec l’estimation (${formatEuros(sentEstimate.annualSavings)}/an)` : ''}`);
          warnings.forEach(w => toast(w, { duration: 8000 }));
          patch.contract_sent_at = new Date().toISOString();
          if (mandateReference) patch.mandate_sent_at = patch.contract_sent_at;
        } else if (!c.contract_sent_at) {
          patch.contract_sent_at = new Date().toISOString();
        }
      }
      if (crosses('contract_signed')) {
        patch.contract_signed_at = signedDate;
        if (signedFile) patch.signed_contract_path = await uploadSignedContract(c.id, signedFile);
        if (mandatePending && mandateSigned) {
          // Le contrat signé contient le mandat : il sert de mandat signé.
          patch.mandate_status = 'active';
          patch.mandate_signed_at = mandateDate ?? signedDate;
          if (patch.signed_contract_path) patch.signed_mandate_path = patch.signed_contract_path;
          // Contrat avec mandat signé : les factures passent en domiciliation.
          patch.billing_channel = 'direct_debit';
        }
      }
      if (crosses('sibelga')) {
        patch.sibelga_encoded_at = encodedDate;
        patch.sharing_operation_id = operationId;
        if (target === 'sibelga') patch.sibelga_status = sibelgaStatus;
      }
      if (crosses('active')) {
        patch.sharing_start_date = startDate;
        patch.sharing_price_cents_kwh = Number(price);
        if (isProsumer) patch.sharing_sell_price_cents_kwh = Number(sellPrice);
        patch.sharing_vat_rate = vatRate;
        if (c.role === 'producer' || c.role === 'prosumer') patch.reimbursement_period = c.reimbursement_period ?? 'quarterly';
      }

      // Membre actif : fiches participants (facturation, inscription par EAN), rangées dans le groupe prévu.
      if (target === 'active') {
        const planned = targetGroupFor({ ...c, ...patch } as CrmContact);
        const card = planned ? await findGroupCard(planned, c.id) : null;
        const groupe = card?.groupe ?? knownGroup?.name ?? planned;
        const meters = pendingMeters > 0 ? await createParticipantsForContact({ ...c, ...patch } as CrmContact, groupe) : c.meters;
        if (card) {
          // Groupe existant : les EAN rejoignent la carte du groupe, la fiche de la société est conservée dans les archivés.
          const { card: updatedCard, merged } = await mergeIntoGroupCard(c, card, meters, patch);
          toast.success(`${name} rattaché au groupe « ${card.groupe} »`);
          const final = await runActions(merged, true);
          onDone(final, [updatedCard]);
          return;
        }
        patch.meters = meters;
        if (groupe) patch.groupe = groupe;
      }

      const saved = await updateContact(c.id, patch);
      toast.success(`${name} → ${targetLabel}`);
      if (target === 'active' && pendingMeters > 0) {
        toast('Fiches participants créées : complétez les tarifs fournisseur dans le tableau de bord de l’opération.', { duration: 6000 });
      }
      onDone(await runActions(saved, false));
    } catch (err) {
      toast.error(`Erreur : ${errorMessage(err)}`);
    } finally {
      setWorking(false);
    }
  };

  /**
   * Actions cochées, après l'enregistrement de l'étape. Une action en échec n'annule pas le changement d'étape.
   * Groupe existant : l'onboarding et l'invitation se lancent depuis la fiche du groupe.
   */
  const runActions = async (contact: CrmContact, groupMerged: boolean): Promise<CrmContact> => {
    let current = contact;
    const chosen = messageActions.filter(a => actionChecks[a.id]);
    if (chosen.length === 0) return current;
    if (!current.email?.trim()) {
      toast.error('Actions non exécutées : le contact n’a pas d’adresse e-mail');
      return current;
    }
    for (const a of chosen) {
      try {
        if (a.type === 'send_message') {
          await sendCustomMessage(current, operations, edits[a.id] ?? { subject: a.subject ?? '', body: a.body ?? '' });
        } else if (groupMerged) {
          toast(`${actionLabel(a)} : à lancer depuis la fiche du groupe`, { duration: 6000 });
          continue;
        } else if (a.type === 'send_welcome') {
          await sendWelcome(current, operations, edits[a.id] ?? undefined);
          current = await updateContact(current.id, { onboarding_sent_at: new Date().toISOString() });
        } else if (a.type === 'send_invitation') {
          await sendInvitation(current, operations, edits[a.id] ?? undefined);
          current = await updateContact(current.id, { invitation_sent_at: new Date().toISOString() });
        }
        toast.success(`${actionLabel(a)} : envoyé à ${current.email}`);
      } catch (err) {
        toast.error(`${actionLabel(a)} : ${errorMessage(err)}`);
      }
    }
    return current;
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const { pdfBase64, fileName, warnings } = await generateContract(c.id, { sepa: withMandate });
      downloadBase64Pdf(pdfBase64, fileName);
      warnings?.forEach(w => toast(w, { duration: 8000 }));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setPreviewing(false);
    }
  };

  /** Fiche telle qu'elle sera après ce passage d'étape : sert à l'aperçu des messages (date de début, prix, opération…). */
  const previewContact: CrmContact = {
    ...c,
    ...(crosses('contract_signed') ? { contract_signed_at: signedDate } : {}),
    ...(crosses('sibelga') ? { sibelga_encoded_at: encodedDate, sharing_operation_id: operationId || c.sharing_operation_id } : {}),
    ...(crosses('active') ? {
      sharing_start_date: startDate || c.sharing_start_date,
      sharing_price_cents_kwh: validPrice(price) ? Number(price) : c.sharing_price_cents_kwh,
      ...(isProsumer && validPrice(sellPrice) ? { sharing_sell_price_cents_kwh: Number(sellPrice) } : {}),
    } : {}),
  };
  const previewKey = [signedDate, operationId, startDate, price, sellPrice].join('|');

  const cancelBtn = <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600 hover:bg-neutral-100">Annuler</button>;

  if (backward) {
    return (
      <ModalShell
        title={`Revenir à « ${targetLabel} » ?`}
        subtitle={name}
        onClose={onCancel}
        footer={<>{cancelBtn}
          <button onClick={confirmBackward} disabled={working} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700 disabled:opacity-50">
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeft className="w-4 h-4" />}Confirmer le retour
          </button></>}
      >
        <p className="text-sm text-neutral-600">Les informations déjà saisies (dates, contrat, opération) sont conservées.</p>
        {c.meters.some(m => m.participant_id) && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-3">
            Les fiches participants (facturation) ne sont pas supprimées. Retirez-les depuis le tableau de bord de l’opération si nécessaire.
          </p>
        )}
      </ModalShell>
    );
  }

  return (
    <ModalShell
      title={`Passer à « ${targetLabel} »`}
      subtitle={name}
      onClose={onCancel}
      footer={<>{cancelBtn}
        <button onClick={confirmForward} disabled={working} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-neutral-900 text-sm font-semibold disabled:opacity-50">
          {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {target === 'contract_signed' ? 'Oui, signé' : 'Confirmer'}
        </button></>}
    >
      <div className="space-y-5">
        {crosses('contract_sent') && target === 'contract_sent' && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-neutral-800">Envoyer le contrat par e-mail ?</p>
            <div className="grid grid-cols-2 gap-2">
              {[true, false].map(v => (
                <button key={String(v)} type="button" onClick={() => { sendTouched.current = true; setSendByEmail(v); }}
                  className={`py-2 rounded-lg text-xs font-semibold border ${sendByEmail === v ? 'border-[#F8BD3D] bg-amber-50 text-neutral-900' : 'border-neutral-200 text-neutral-500'}`}>
                  {v ? 'Oui, envoyer' : 'Non, déjà transmis'}
                </button>
              ))}
            </div>
            {sendByEmail && <p className="flex items-center gap-1.5 text-xs text-neutral-500"><Mail className="w-3.5 h-3.5" />Destinataire : {c.email}</p>}
            {sendByEmail && sepaEligible && (
              <div className={`p-2.5 rounded-lg border ${withMandate ? 'border-[#F8BD3D] bg-amber-50/60' : 'border-neutral-200'}`}>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={includeMandate} onChange={e => setIncludeMandate(e.target.checked)} />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-neutral-800"><Landmark className="w-3.5 h-3.5 text-neutral-500" />Inclure le mandat SEPA</span>
                    <span className="block text-[11px] text-neutral-500 mt-0.5">
                      {c.sepa_consent ? 'Le membre a accepté la domiciliation.' : 'Le membre n’a pas coché l’accord pour la domiciliation.'}
                      {' '}Modèle « avec mandat SEPA », référence {c.mandate_reference && c.mandate_status !== 'revoked' ? c.mandate_reference : 'attribuée à l’envoi'}.
                      {mandate === 'active' && ' Un mandat est déjà actif pour ce membre.'}
                    </span>
                  </span>
                </label>
                {withMandate && (bankProblems.length > 0
                  ? <p className="mt-1.5 text-[11px] text-red-700">À compléter dans la fiche : {bankProblems.join(', ')}.</p>
                  : <p className="mt-1.5 text-[11px] text-neutral-600">Compte prélevé : {maskIban(c.iban)} · {c.account_holder} · BIC {c.bic}</p>)}
              </div>
            )}
            {sendByEmail && c.estimate_requested && savings && (
              estimate && savings.enabled
                ? <p className="text-[11px] text-neutral-600 bg-amber-50/60 border border-[#F8BD3D] rounded-lg p-2.5">
                    Estimation des économies ajoutée à l’e-mail : environ <b>{formatEuros(estimate.annualSavings)} par an</b>
                    {' '}({formatKwh(estimate.consumptionKwh)}, {estimate.priceSource === 'declared' ? 'prix déclaré' : 'prix par défaut'} {formatPrice(estimate.pricePerKwh)}).
                  </p>
                : <p className="text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
                    Le membre a demandé une estimation de ses économies : non ajoutée à l’e-mail
                    {savings.enabled ? ' (consommation manquante ou profil non concerné).' : ' (désactivée dans Paramètres → Estimation des économies).'}
                  </p>
            )}
            {c.meters.length > 1 && <p className="text-xs text-neutral-500">Un seul contrat, avec les {c.meters.length} EAN en annexe.</p>}
            {sendByEmail && (
              <EmailPreview
                load={() => previewContractEmail(c, operations, { sepa: withMandate })}
                loadKey={String(withMandate)}
                edited={edits.contract ?? null}
                onEdit={setEdit('contract')}
                attachment={withMandate ? 'contrat PDF avec mandat SEPA' : 'contrat PDF'}
              />
            )}
            <button onClick={handlePreview} disabled={previewing} className="flex items-center gap-1.5 text-xs font-medium text-neutral-700 hover:text-neutral-900">
              {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}Vérifier le PDF avant envoi
            </button>
          </div>
        )}

        {crosses('contract_sent') && target !== 'contract_sent' && (
          <p className="text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
            L’étape « Contrat envoyé » est franchie sans envoi automatique du contrat.
          </p>
        )}

        {crosses('contract_signed') && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-800">Le contrat est-il signé ?</p>
            <Field label="Date de signature" required>
              <input type="date" value={signedDate} onChange={e => setSignedDate(e.target.value)} className={`${inputClass} border-neutral-200`} />
            </Field>
            <Field label="Contrat signé (PDF, facultatif)">
              <input type="file" accept="application/pdf" onChange={e => setSignedFile(e.target.files?.[0] ?? null)} className="block w-full text-xs text-neutral-600" />
            </Field>
            {mandatePending && (
              <div className={`p-2.5 rounded-lg border space-y-2 ${mandateSigned ? 'border-[#F8BD3D] bg-amber-50/60' : 'border-neutral-200'}`}>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={mandateSigned} onChange={e => setMandateSigned(e.target.checked)} />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-neutral-800"><Landmark className="w-3.5 h-3.5 text-neutral-500" />Le mandat SEPA {c.mandate_reference} est signé</span>
                    <span className="block text-[11px] text-neutral-500 mt-0.5">Le mandat devient actif ; le contrat signé déposé sert de mandat signé. Les factures passent en domiciliation.</span>
                  </span>
                </label>
                {mandateSigned && (
                  <Field label="Date de signature du mandat">
                    <input type="date" value={mandateDate ?? signedDate} onChange={e => setMandateDate(e.target.value)} className={`${inputClass} border-neutral-200`} />
                  </Field>
                )}
              </div>
            )}
          </div>
        )}

        {crosses('sibelga') && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-800">Encodage Sibelga</p>
            <p className="text-[11px] text-neutral-500">Date d’encodage enregistrée : {encodedDate.split('-').reverse().join('/')} (modifiable dans la fiche).</p>
            <Field label="Opération de partage" required>
              <select value={operationId} onChange={e => setOperationId(e.target.value)} className={`${inputClass} border-neutral-200`}>
                <option value="">Sélectionner…</option>
                {operations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </Field>
            {target === 'sibelga' && (
              <div className="space-y-1.5">
                <p className="text-[11px] font-medium text-neutral-600">Statut Sibelga (plusieurs choix possibles)</p>
                <SibelgaStatusPicker value={sibelgaStatus} onChange={setSibelgaStatus} />
              </div>
            )}
          </div>
        )}

        {crosses('active') && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-800">Activation du partage</p>
            <Field label="Date de début de partage" required>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`${inputClass} border-neutral-200`} />
            </Field>
            <p className="text-[10px] text-neutral-400 -mt-2">Proposée : 1er du mois suivant l’encodage</p>
            <Field label={isProsumer ? "Prix d'achat appliqué (c€/kWh HTVA)" : 'Prix appliqué (c€/kWh HTVA)'} required>
              <input type="number" step="any" value={price} onChange={e => { priceTouched.current = true; setPrice(e.target.value); }} className={`${inputClass} border-neutral-200`} />
            </Field>
            {isProsumer && (
              <Field label="Prix de vente appliqué (c€/kWh HTVA)" required>
                <input type="number" step="any" value={sellPrice} onChange={e => { priceTouched.current = true; setSellPrice(e.target.value); }} className={`${inputClass} border-neutral-200`} />
              </Field>
            )}
            <p className="text-[10px] text-neutral-400 -mt-2">
              {groupPriceApplied && knownGroup
                ? `Prix du groupe « ${knownGroup.name} »${contractPrice !== null && String(contractPrice) !== price ? ` (prix du contrat : ${contractPrice} c€/kWh)` : ''}`
                : 'Pré-rempli avec le prix du contrat'}
            </p>
            <Field label="TVA appliquée" required>
              <select value={String(vatRate)} onChange={e => setVatRate(Number(e.target.value))} className={`${inputClass} border-neutral-200`}>
                {VAT_RATE_OPTIONS.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
              </select>
            </Field>
            <p className="text-[10px] text-neutral-400 -mt-2">Proposée : 6 % pour une personne physique, 21 % pour une société ; producteur selon son régime TVA</p>
            {c.meters.some(m => !m.participant_id) && (
              <div className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
                <p className="font-medium text-neutral-700 mb-1">Fiches participants à créer</p>
                <ul className="space-y-0.5">
                  {c.meters.filter(m => !m.participant_id).map(m => (
                    <li key={m.ean_code} className="flex justify-between gap-2">
                      <span className="truncate">{m.name || 'Sans nom'}</span>
                      <span className="font-mono text-[10px] text-neutral-400">{m.ean_code}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pendingMeters > 0 && (
              <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
                {pendingMeters} fiche{pendingMeters > 1 ? 's' : ''} participant (facturation) {pendingMeters > 1 ? 'seront créées' : 'sera créée'}, une par EAN
                {plannedGroup
                  ? knownGroup
                    ? `, et rattachée${pendingMeters > 1 ? 's' : ''} au groupe existant « ${knownGroup.name} ».`
                    : `, dans le nouveau groupe « ${plannedGroup} ».`
                  : '.'}
              </p>
            )}
            {groupCard && (
              <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg p-2.5">
                {contactPatch.email
                  ? `La carte du groupe n’a pas encore de contact : ${contactPatch.email} deviendra le contact du groupe.`
                  : groupCard.email
                    ? `Contact du groupe : ${groupCard.email} (inchangé, modifiable dans la fiche du groupe).`
                    : 'Le groupe n’a pas encore de contact : ajoutez un e-mail dans la fiche du groupe.'}
              </p>
            )}
          </div>
        )}

        {messageActions.length > 0 && (
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-sm font-medium text-neutral-800"><Zap className="w-4 h-4 text-amber-500" />Actions au passage à l’étape</p>
            <div className="space-y-1.5">
              {messageActions.map(a => (
                <div key={a.id} className="rounded-lg border border-neutral-200">
                  <label className="flex items-start gap-2.5 p-2.5 cursor-pointer hover:bg-neutral-50 rounded-lg">
                    <input type="checkbox" className="mt-0.5" checked={!!actionChecks[a.id]} disabled={!c.email}
                      onChange={e => setActionChecks(prev => ({ ...prev, [a.id]: e.target.checked }))} />
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-neutral-800">{actionLabel(a)}</span>
                      <span className="block text-[11px] text-neutral-500 truncate">
                        {a.type === 'send_message' ? `Objet : ${a.subject}` : TRIGGER_TYPES[a.type].description}
                      </span>
                    </span>
                  </label>
                  {actionChecks[a.id] && (
                    <div className="px-2.5 pb-2.5 pl-8">
                      <EmailPreview
                        load={() => previewMessage(previewContact, operations,
                          a.type === 'send_message' ? { subject: a.subject ?? '', body: a.body ?? '' } : a.type === 'send_welcome' ? 'welcome' : 'invitation')}
                        loadKey={previewKey}
                        edited={edits[a.id] ?? null}
                        onEdit={setEdit(a.id)}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-neutral-500">
              {c.email ? <>Destinataire : {c.email}. </> : <span className="text-amber-700">Pas d’adresse e-mail : aucun message ne partira. </span>}
              Actions réglables dans CRM → Paramètres → Déclencheurs.
            </p>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
